import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dataAPI } from '../services/api';
import './Absences.css';

const HUBFIT_API_URL = process.env.REACT_APP_API_URL || 'https://apidashboard.teamgreekgods.ma/api';
const MONTHS = ['', 'Janvier', 'Fevrier', 'Mars', 'Avri', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const Absences = () => {
  const [absences, setAbsences] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [formData, setFormData] = useState({
    assistant_id: '',
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    replacement_1: '',
    replacement_2: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [empRes, absRes] = await Promise.all([
        axios.get(`${HUBFIT_API_URL}/employees`, {
          headers: { Authorization: `Bearer ${process.env.REACT_APP_TOKEN}` }
        }),
        dataAPI.getAbsences()
      ]);

      if (empRes.data.success) {
        const employees = empRes.data.data || [];
        const formattedAssistants = employees
          .filter((emp) => emp.is_active === 1)
          .map((emp) => ({
            id: emp.store_ids?.[0]?.id || emp.store_id,
            name: `${emp.first_name} ${emp.last_name}`,
            store: emp.store_ids?.[0]?.name || `WA ${emp.store_ids?.[0]?.code || emp.store_id}`
          }));
        setAssistants(formattedAssistants);
      }

      if (absRes.data.success) {
        setAbsences(absRes.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load data', error);
    }
  };

  const saveData = async (updatedAbsences) => {
    try {
      const response = await dataAPI.saveAbsences(updatedAbsences);
      console.log('Save response:', response.data);
    } catch (error) {
      console.error('Failed to save', error);
    }
  };

  const getSelectedDate = () => {
    if (!selectedDay) return null;
    return `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
  };

  const getAbsencesForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return absences.filter((a) => dateStr >= a.from_date && dateStr <= a.to_date);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDay(day);
  };

  const openModalForDay = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setFormData({
      assistant_id: assistants[0]?.id || '',
      from_date: dateStr,
      to_date: dateStr,
      replacement_1: '',
      replacement_2: ''
    });
    setEditingAbsence(null);
    setShowModal(true);
  };

  const openEditModal = (absence) => {
    const assistant = assistants.find((a) => a.name === absence.assistant_name);
    setFormData({
      assistant_id: assistant?.id || '',
      from_date: absence.from_date,
      to_date: absence.to_date,
      replacement_1: assistants.find((a) => a.name === absence.replacement_1)?.id || '',
      replacement_2: assistants.find((a) => a.name === absence.replacement_2)?.id || ''
    });
    setEditingAbsence(absence);
    setShowModal(true);
  };

  const openModal = () => {
    setFormData({
      assistant_id: assistants[0]?.id || '',
      from_date: new Date().toISOString().slice(0, 10),
      to_date: new Date().toISOString().slice(0, 10),
      replacement_1: '',
      replacement_2: ''
    });
    setEditingAbsence(null);
    setShowModal(true);
  };

  const handleSave = () => {
    const selectedAssistant = assistants.find((a) => a.id === parseInt(formData.assistant_id));
    const replacement1 = assistants.find((a) => a.id === parseInt(formData.replacement_1));
    const replacement2 = assistants.find((a) => a.id === parseInt(formData.replacement_2));

    const absenceData = {
      ...formData,
      assistant_name: selectedAssistant?.name || '',
      replacement_1: replacement1?.name || '',
      replacement_2: replacement2?.name || ''
    };

    let updated;
    if (editingAbsence) {
      updated = absences.map((a) => a.id === editingAbsence.id ? { ...a, ...absenceData } : a);
    } else {
      updated = [...absences, { ...absenceData, id: Date.now() }];
    }
    
    setAbsences(updated);
    saveData(updated);
    setShowModal(false);
    setSelectedDay(null);
  };

  const handleDelete = (id) => {
    const updated = absences.filter((a) => a.id !== id);
    setAbsences(updated);
    saveData(updated);
  };

  const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month - 1, 1).getDay();

  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }

    return days;
  };

const isAbsenceDay = (day) => {
    if (!day) return false;
    return getAbsencesForDay(day).length > 0;
  };

  const today = new Date();
  const isToday = (day) => day === today.getDate() && currentMonth === today.getMonth() + 1 && currentYear === today.getFullYear();
  const calendarDays = generateCalendar();

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  return (
    <div className="absences-page">
      <div className="section-header">
        <div className="section-dot"></div>
        <h2>Absences et Planning</h2>
        <button className="btn-primary ml-auto" onClick={openModal}>+ Planifier</button>
      </div>

      <div className="absences-layout">
        <div className="calendar-section">
          <div className="mini-card">
            <div className="cal-header">
              <button className="cal-nav" onClick={prevMonth}>&#8249;</button>
              <div className="cal-label">{MONTHS[currentMonth]} {currentYear}</div>
              <button className="cal-nav" onClick={nextMonth}>&#8250;</button>
            </div>
            <div className="cal-grid">
              {DAYS.map((d) => <div key={d} className="dow">{d}</div>)}
              {calendarDays.map((day, idx) => {
                const dayAbsences = getAbsencesForDay(day);
                return (
                  <div
                    key={idx}
                    className={`cal-day ${!day ? 'empty' : ''} ${day && isAbsenceDay(day) ? 'absence' : ''} ${isToday(day) ? 'today' : ''} ${selectedDay === day ? 'selected' : ''}`}
                    onClick={() => day && handleDayClick(day)}
                  >
                    {day && <span>{day}</span>}
                    {day && dayAbsences.length > 0 && (
                      <div className="absence-indicator" title={dayAbsences.map(a => a.assistant_name).join(', ')}></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="legend">
            <span className="legend-item">
              <span className="legend-dot"></span>
              Absence
            </span>
          </div>
        </div>

        <div className="list-section">
          <div className="list-header">
            <div className="list-title">
              {selectedDay ? `Absences du ${getSelectedDate()}` : 'Absences planifiees'}
            </div>
            {selectedDay ? (
              <button className="btn-add-day" onClick={() => openModalForDay(selectedDay)}>
                + Planifier ce jour
              </button>
            ) : (
              <button className="btn-add-day" onClick={openModal}>
                + Planifier
              </button>
            )}
          </div>
          <div className="absence-list">
            {(selectedDay ? getAbsencesForDay(selectedDay) : absences).length === 0 ? (
              <div className="no-absences">Aucune absence{selectedDay ? ' ce jour' : ' planifiee'}</div>
            ) : (
              (selectedDay ? getAbsencesForDay(selectedDay) : absences).map((a) => (
                <div key={a.id} className="absence-item">
                  <div className="absence-info">
                    <div className="absence-assistant">{a.assistant_name}</div>
                    <div className="absence-dates">
                      Du {a.from_date} au {a.to_date}
                    </div>
                    {(a.replacement_1 || a.replacement_2) && (
                      <div className="absence-replacements">
                        Remplacant(s): {a.replacement_1}{a.replacement_2 ? `, ${a.replacement_2}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="absence-actions">
                    <button className="edit-btn" onClick={() => openEditModal(a)} title="Modifier">&#9998;</button>
                    <button className="delete-btn" onClick={() => handleDelete(a.id)}>×</button>
                  </div>
                </div>
              ))
            )}
          </div>
          {selectedDay && (
            <button className="btn-clear-selection" onClick={() => setSelectedDay(null)}>
              Afficher toutes les absences
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingAbsence ? 'Modifier absence' : 'Planifier une absence'}</h3>
            <div className="form-group">
              <label>Assistante</label>
              <select value={formData.assistant_id} onChange={(e) => setFormData({ ...formData, assistant_id: e.target.value })}>
                {assistants.map((a) => <option key={a.id} value={a.id}>{a.name} - {a.store}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Du</label>
                <input type="date" value={formData.from_date} onChange={(e) => setFormData({ ...formData, from_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Au</label>
                <input type="date" value={formData.to_date} onChange={(e) => setFormData({ ...formData, to_date: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Remplacante 1 sur WA</label>
              <select value={formData.replacement_1} onChange={(e) => setFormData({ ...formData, replacement_1: e.target.value })}>
                <option value="">- Pas de remplacante</option>
                {assistants.filter((a) => a.id !== parseInt(formData.assistant_id)).map((a) => (
                  <option key={a.id} value={a.id}>{a.name} - {a.store}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Remplacante 2 (optionnel)</label>
              <select value={formData.replacement_2} onChange={(e) => setFormData({ ...formData, replacement_2: e.target.value })}>
                <option value="">- Aucune</option>
                {assistants.filter((a) => a.id !== parseInt(formData.assistant_id) && a.id !== parseInt(formData.replacement_1)).map((a) => (
                  <option key={a.id} value={a.id}>{a.name} - {a.store}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSave}>Enregistrer</button>
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Absences;
